import { useMemo } from 'react';
import { Banner, BlockStack, Card, DataTable, Divider, InlineGrid, Modal, Text } from '@shopify/polaris';
import __helpers from 'helpers/index';

export type OrderInvoicePreviewData = {
  taxType?: string;
  KHMSHDon?: string;
  KHHDon?: string;
  NLap?: string;
  HTTToan?: string;
  MTChieu?: string;
  NMua_Ten?: string;
  NMua_HVTNMHang?: string;
  NMua_SDThoai?: string;
  NMua_MST?: string;
  NMua_CCCDan?: string;
  NMua_DChi?: string;
  NMua_DCTDTu?: string;
  TgThTien?: number;
  TgTThue?: number;
  TgTTTBSo?: number;
  DSHHDVu?: Array<{
    STT?: number;
    THHDVu?: string;
    SLuong?: number;
    DVTinh?: string;
    DGia?: number;
    ThTien?: number;
    TSuat?: number;
    TThue?: number;
    TgTien?: number;
  }>;
};

type OrderInvoicePreviewModalProps = {
  open: boolean;
  data: OrderInvoicePreviewData | null;
  loading?: boolean;
  onClose: () => void;
  onExport: () => void;
};

const displayValue = (value?: string | number) => (value === undefined || value === null || value === '' ? '-' : String(value));
const displayMoney = (value?: number) => (value === undefined || value === null ? '-' : `${value.toLocaleString('vi-VN')} ₫`);

export default function OrderInvoicePreviewModal({ open, data, loading, onClose, onExport }: OrderInvoicePreviewModalProps) {
  const generalInformation = useMemo(
    () => [
      { label: 'Loại hóa đơn', value: displayValue(data?.taxType) },
      { label: 'Mẫu số / Ký hiệu', value: data?.KHMSHDon ? `${data.KHMSHDon} / ${displayValue(data.KHHDon)}` : '-' },
      { label: 'Ngày lập', value: displayValue(data?.NLap) },
      { label: 'Hình thức thanh toán', value: displayValue(data?.HTTToan) },
      { label: 'Mã tham chiếu', value: displayValue(data?.MTChieu) },
    ],
    [data]
  );

  const buyerInformation = useMemo(
    () => [
      { label: 'Tên người mua', value: displayValue(data?.NMua_HVTNMHang) },
      { label: 'Tên đơn vị', value: displayValue(data?.NMua_Ten) },
      { label: 'Mã số thuế', value: displayValue(data?.NMua_MST) },
      { label: 'Số CCCD', value: displayValue(data?.NMua_CCCDan) },
      { label: 'Địa chỉ', value: displayValue(data?.NMua_DChi) },
      { label: 'Số điện thoại', value: displayValue(data?.NMua_SDThoai) },
      { label: 'Email nhận hóa đơn', value: displayValue(data?.NMua_DCTDTu) },
    ],
    [data]
  );

  const lineItemRows = useMemo(
    () =>
      data?.DSHHDVu?.map((item, index) => [
        displayValue(item.STT ?? index + 1),
        displayValue(item.THHDVu),
        displayValue(item.SLuong),
        displayValue(item.DVTinh),
        displayMoney(item.DGia),
        item.TSuat === undefined || item.TSuat === null ? '-' : `${item.TSuat}%`,
        displayMoney(item.ThTien),
        displayMoney(item.TThue),
        displayMoney(item.TgTien),
      ]) || [],
    [data?.DSHHDVu]
  );

  const renderInformation = (items: { label: string; value: string }[]) => (
    <BlockStack gap="200">
      {items.map((item) => (
        <InlineGrid key={item.label} columns={{ xs: '1fr', sm: '1fr 2fr' }} gap="300">
          <Text as="span" variant="bodySm" tone="subdued">
            {item.label}
          </Text>
          <Text as="span" variant="bodySm" fontWeight="semibold">
            {item.value}
          </Text>
        </InlineGrid>
      ))}
    </BlockStack>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Xem trước hóa đơn đơn hàng"
      size="large"
      primaryAction={{ content: 'Tiếp tục xuất', onAction: onExport, loading, disabled: !data }}
      secondaryActions={[{ content: 'Hủy', onAction: onClose, disabled: loading }]}
    >
      <Modal.Section>
        {__helpers.isEmpty(data) ? (
          <Text as="p" tone="subdued">
            Đã xuất hóa đơn, không cần phải thao tác gì thêm.
          </Text>
        ) : (
          <BlockStack gap="400">
            <Banner tone="info">
              <Text as="p">Vui lòng kiểm tra kỹ thông tin. Hóa đơn sẽ được tạo nháp ngay khi bạn tiếp tục. Bản nháp có thể xóa để tạo lại.</Text>
            </Banner>

            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
              <Card padding="400">
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">
                    Thông tin hóa đơn
                  </Text>
                  {renderInformation(generalInformation)}
                </BlockStack>
              </Card>
              <Card padding="400">
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">
                    Thông tin người mua
                  </Text>
                  {renderInformation(buyerInformation)}
                </BlockStack>
              </Card>
            </InlineGrid>

            <Card padding="400">
              <BlockStack gap="300">
                <Text as="h3" variant="headingMd">
                  Tổng thanh toán
                </Text>
                <DataTable
                  hideScrollIndicator
                  columnContentTypes={['text', 'numeric']}
                  headings={['Khoản mục', 'Số tiền']}
                  rows={[
                    ['Tổng tiền trước thuế', displayMoney(data?.TgThTien)],
                    ['Tổng tiền thuế', displayMoney(data?.TgTThue)],
                    ['Tổng cộng thanh toán', displayMoney(data?.TgTTTBSo)],
                  ]}
                />
              </BlockStack>
            </Card>

            <Divider />
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Hàng hóa, dịch vụ
              </Text>
              {lineItemRows.length ? (
                <Card padding="0">
                  <DataTable
                    verticalAlign="middle"
                    hideScrollIndicator
                    hasZebraStripingOnData
                    columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                    headings={['STT', 'Hàng hóa/Dịch vụ', 'Số lượng', 'ĐVT', 'Đơn giá', 'Thuế suất', 'Tiền hàng', 'Tiền thuế', 'Tổng']}
                    rows={lineItemRows}
                  />
                </Card>
              ) : (
                <Text as="p" tone="subdued">
                  Chưa có dữ liệu hàng hóa/dịch vụ.
                </Text>
              )}
            </BlockStack>
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

